import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faScrewdriverWrench, faHandHolding, faFolderOpen,
  faArrowsRotate, faPlus, faAsterisk,
} from '@fortawesome/free-solid-svg-icons';
import '../../styles/GizmosBoard.css';

const ENERGY_COLORS = ['RED', 'YELLOW', 'BLUE', 'BLACK'];
const COLOR_LABEL = { RED: '红', YELLOW: '黄', BLUE: '蓝', BLACK: '黑' };
const COLOR_CLASS = { RED: 'gem-red', YELLOW: 'gem-yellow', BLUE: 'gem-blue', BLACK: 'gem-black' };
const TARGET_LABEL = { LEVEL2: '建T2', ARCHIVE: '从档案建造', RESEARCH: '研究建造', FILE: '归档' };

const EFFECT_LABEL = {
  give_energy: (e) => `+${e.amount}${COLOR_LABEL[e.color] || e.color}`,
  give_any: (e) => `+${e.amount}任意能量`,
  give_random: (e) => `盲抽${e.amount}能量`,
  give_vp: (e) => `+${e.amount}VP标记`,
  extra_pick: (e) => `额外拾取×${e.amount}`,
  extra_file: (e) => `额外归档×${e.amount}`,
  extra_research: (e) => `额外研究×${e.amount}`,
  extra_build_free: (e) => `免费建造1张T${e.tier}`,
  converter_any: (e) => `${COLOR_LABEL[e.color]}→任意 ×${e.amount}`,
  converter_wild: () => '任意→任意 ×1',
  converter_double: (e) => `倍增 ${(e.colors || []).map(c => COLOR_LABEL[c]).join('/')}`,
  discount: (e) => `${TARGET_LABEL[e.target] || e.target} -${e.amount}费`,
  negate: (e) => `放弃「${TARGET_LABEL[e.target] || e.target}」动作`,
  upgrade: (e) => ['storage', 'archive', 'research']
    .filter(k => e[k] > 0)
    .map(k => `+${e[k]}${{ storage: '储能', archive: '档案', research: '研究' }[k]}`)
    .join(' '),
  upgrade_storage: (e) => `+${e.amount}储能上限`,
  upgrade_archive: (e) => `+${e.amount}档案上限`,
  upgrade_research: (e) => `+${e.amount}研究深度`,
  vp_per_tier: (e) => `每T${e.tier}装置 +${e.vp_each}VP`,
  vp_per_color: (e) => `每${COLOR_LABEL[e.color]}装置 +${e.vp_each}VP`,
  vp_per_energy: (e) => `终局每能量 +${e.vp_each}VP`,
  vp_per_vp_token: (e) => `终局每VP标记 +${e.vp_each}VP`,
};

const EFFECT_CATEGORY = {
  give_energy: 'trigger', give_any: 'trigger', give_random: 'trigger',
  give_vp: 'trigger', extra_pick: 'trigger', extra_file: 'trigger',
  extra_research: 'trigger', extra_build_free: 'trigger',
  converter_any: 'converter', converter_wild: 'converter', converter_double: 'converter',
  discount: 'discount', negate: 'negate',
  upgrade: 'upgrade', upgrade_storage: 'upgrade',
  upgrade_archive: 'upgrade', upgrade_research: 'upgrade',
  vp_per_tier: 'formula', vp_per_color: 'formula',
  vp_per_energy: 'formula', vp_per_vp_token: 'formula',
};

const TRIGGER_LABEL = { BUILD: '建造', PICK: '拾取', FILE: '归档' };
const CONVERTER_TYPES = ['converter_any', 'converter_wild', 'converter_double'];

// -----------------------------------------------------------------------
// Rule helpers (mirror gizmos_core semantics)
// -----------------------------------------------------------------------

const isGeneric = (g) => 'ANY' in (g.cost || {});
const cardColors = (g) =>
  isGeneric(g) ? ENERGY_COLORS : Object.keys(g.cost || {}).filter(c => ENERGY_COLORS.includes(c));
const totalEnergy = (ps) => Object.values(ps.energy || {}).reduce((a, b) => a + b, 0);

function computeVp(ps) {
  let total = (ps.vp || 0) + (ps.vp_tokens || 0);
  for (const g of ps.built_gizmos || []) {
    const e = g.effect;
    if (!e) continue;
    if (e.type === 'vp_per_tier') {
      total += e.vp_each * ps.built_gizmos.filter(b => b.tier === e.tier).length;
    } else if (e.type === 'vp_per_color') {
      total += e.vp_each * ps.built_gizmos.filter(b => cardColors(b).includes(e.color)).length;
    } else if (e.type === 'vp_per_energy') {
      total += e.vp_each * totalEnergy(ps);
    } else if (e.type === 'vp_per_vp_token') {
      total += e.vp_each * (ps.vp_tokens || 0);
    }
  }
  return total;
}

function discountedCost(gizmo, myState, source) {
  let discount = 0;
  for (const g of myState?.built_gizmos || []) {
    const e = g.effect;
    if (!e || e.type !== 'discount') continue;
    if ((e.target === 'LEVEL2' && gizmo.tier === 2)
      || (e.target === 'ARCHIVE' && source === 'archive')
      || (e.target === 'RESEARCH' && source === 'research')) {
      discount += e.amount;
    }
  }
  const cost = { ...gizmo.cost };
  if (!discount) return cost;
  for (const k of Object.keys(cost)) {
    const cut = Math.min(cost[k], discount);
    cost[k] -= cut;
    discount -= cut;
    if (!discount) break;
  }
  return cost;
}

function availableConverters(myState, usedIds) {
  return (myState?.built_gizmos || []).filter(g =>
    g.effect && CONVERTER_TYPES.includes(g.effect.type) && !usedIds.includes(g.id));
}

// Simulate the declared payment chain; returns {working, valid}
function simulatePlan(energy, conversions, converters) {
  const working = {};
  ENERGY_COLORS.forEach(c => { working[c] = energy[c] || 0; });
  for (const step of conversions) {
    const conv = converters.find(g => g.id === step.gizmo_id);
    const isDouble = conv?.effect?.type === 'converter_double';
    if ((working[step.from_color] || 0) < 1) return { working, valid: false };
    if (isDouble) {
      working[step.from_color] += 1;
    } else {
      working[step.from_color] -= 1;
      working[step.to_color] = (working[step.to_color] || 0) + 1;
    }
  }
  return { working, valid: true };
}

function paysExactly(working, cost) {
  if ('ANY' in cost) {
    return Object.values(working).reduce((a, b) => a + b, 0) === cost.ANY;
  }
  return ENERGY_COLORS.every(c => (working[c] || 0) === (cost[c] || 0));
}

// -----------------------------------------------------------------------
// Small display components
// -----------------------------------------------------------------------

function EnergyBall({ color, size = 28, onClick, clickable, disabled }) {
  return (
    <span
      className={`energy-ball ${COLOR_CLASS[color]} ${clickable ? 'clickable' : ''} ${disabled ? 'disabled' : ''}`}
      style={{ width: size, height: size, lineHeight: `${size}px`, fontSize: size * 0.4 }}
      title={COLOR_LABEL[color]}
      onClick={clickable && !disabled ? onClick : undefined}
    >
      {COLOR_LABEL[color]}
    </span>
  );
}

function CostRow({ cost, size = 18 }) {
  if ('ANY' in cost) {
    return (
      <div className="gizmo-cost">
        <span className="any-cost-badge" title="任意颜色组合">任意×{cost.ANY}</span>
      </div>
    );
  }
  return (
    <div className="gizmo-cost">
      {Object.entries(cost).map(([c, n]) => (
        Array.from({ length: n }, (_, i) => (
          <EnergyBall key={`${c}-${i}`} color={c} size={size} />
        ))
      ))}
    </div>
  );
}

function triggerText(gizmo) {
  if (!gizmo.trigger) return '';
  let t = TRIGGER_LABEL[gizmo.trigger] || gizmo.trigger;
  const parts = [];
  if (gizmo.trigger_colors?.length) {
    parts.push(gizmo.trigger_colors.map(c => COLOR_LABEL[c]).join('/'));
  }
  if (gizmo.trigger_tier != null) parts.push(`T${gizmo.trigger_tier}`);
  if (gizmo.trigger_from_archive) parts.push('从档案');
  return parts.length ? `${parts.join(' ')} ${t}时` : `${t}时`;
}

function EffectTag({ gizmo }) {
  const effect = gizmo.effect;
  if (!effect) return null;
  const label = EFFECT_LABEL[effect.type]?.(effect) || effect.type;
  const category = EFFECT_CATEGORY[effect.type] || 'trigger';
  const trigger = triggerText(gizmo);
  return (
    <span className={`effect-tag effect-${category}`}>
      {trigger && <span className="trigger-part">[{trigger}]</span>} {label}
    </span>
  );
}

// -----------------------------------------------------------------------
// Card art — one fixed Font Awesome logo per effect category, rendered as
// a centered low-opacity watermark behind the card content
// -----------------------------------------------------------------------

function artCategory(gizmo) {
  if (gizmo.trigger === 'FILE') return 'file';
  if (gizmo.trigger === 'PICK') return 'pick';
  if (gizmo.trigger === 'BUILD') return 'build';
  const cat = EFFECT_CATEGORY[gizmo.effect?.type];
  if (cat === 'converter') return 'converter';
  if (cat === 'formula') return 'formula';
  return 'upgrade';   // upgrade / discount / negate
}

const ART_ICON = {
  build: faScrewdriverWrench,
  pick: faHandHolding,
  file: faFolderOpen,
  converter: faArrowsRotate,
  upgrade: faPlus,
  formula: faAsterisk,
};

// Official-style layout: banner (small icon + effect text + VP), a big
// category icon in the middle, thick cost-colored border, and a cost dot
// with the amount at the bottom-left.
function GizmoCard({ gizmo, dimmed }) {
  const category = artCategory(gizmo);
  const generic = isGeneric(gizmo);
  const costColor = generic
    ? null
    : Object.keys(gizmo.cost || {}).find(c => ENERGY_COLORS.includes(c)) || null;
  const costAmount = generic ? gizmo.cost.ANY : (costColor ? gizmo.cost[costColor] : 0);
  const borderClass = generic ? 'cost-any' : costColor ? `cost-${costColor.toLowerCase()}` : 'cost-none';

  return (
    <div className={`gizmo-card ${borderClass} ${dimmed ? 'dimmed' : ''}`}>
      <div className="card-banner">
        <FontAwesomeIcon icon={ART_ICON[category]} className={`banner-icon art-${category}`} />
        <span className="banner-text"><EffectTag gizmo={gizmo} /></span>
        <span className="gizmo-vp" title="胜利点数">{gizmo.vp}</span>
      </div>
      <div className={`card-center art-${category}`} aria-hidden="true">
        {category === 'pick' && <span className="pick-orb" />}
        <FontAwesomeIcon icon={ART_ICON[category]} className="center-icon" />
      </div>
      <div className="card-bottom">
        {costAmount > 0 ? (
          <span className={`cost-dot ${generic ? 'any-dot' : COLOR_CLASS[costColor]}`}
            title={generic ? `任意颜色组合×${costAmount}` : `需要${costAmount}个${COLOR_LABEL[costColor]}能量`}>
            {costAmount}
          </span>
        ) : <span />}
        <span className="card-meta">T{gizmo.tier}·#{gizmo.id}</span>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Build modal — explicit payment declaration
// -----------------------------------------------------------------------

function BuildModal({ gizmo, source, myState, usedIds, onConfirm, onCancel }) {
  const cost = useMemo(() => discountedCost(gizmo, myState, source), [gizmo, myState, source]);
  const originalTotal = Object.values(gizmo.cost).reduce((a, b) => a + b, 0);
  const costTotal = Object.values(cost).reduce((a, b) => a + b, 0);
  const converters = useMemo(() => availableConverters(myState, usedIds), [myState, usedIds]);
  const playerEnergy = myState?.energy || {};

  // Pre-fill the direct payment where possible
  const [energy, setEnergy] = useState(() => {
    const init = {};
    ENERGY_COLORS.forEach(c => {
      init[c] = 'ANY' in cost ? 0 : Math.min(cost[c] || 0, playerEnergy[c] || 0);
    });
    return init;
  });
  const [conversions, setConversions] = useState([]);
  // Per-converter pending color selections
  const [convDraft, setConvDraft] = useState({}); // {gizmoId: {from, to}}

  const { working, valid } = useMemo(
    () => simulatePlan(energy, conversions, converters),
    [energy, conversions, converters],
  );
  const exact = valid && paysExactly(working, cost);

  const usesLeft = (g) => {
    const used = conversions.filter(s => s.gizmo_id === g.id);
    if (g.effect.type === 'converter_any') return g.effect.amount - used.length;
    if (g.effect.type === 'converter_wild') return 1 - used.length;
    return (g.effect.colors || []).filter(c => !used.some(s => s.from_color === c));
  };

  const adjustEnergy = (c, delta) => {
    setEnergy(prev => {
      const next = Math.max(0, Math.min((playerEnergy[c] || 0), (prev[c] || 0) + delta));
      return { ...prev, [c]: next };
    });
  };

  const addConversion = (g) => {
    const draft = convDraft[g.id] || {};
    const type = g.effect.type;
    let step = null;
    if (type === 'converter_any') {
      if (!draft.to) return;
      step = { gizmo_id: g.id, from_color: g.effect.color, to_color: draft.to };
    } else if (type === 'converter_wild') {
      if (!draft.from || !draft.to) return;
      step = { gizmo_id: g.id, from_color: draft.from, to_color: draft.to };
    } else {
      if (!draft.from) return;
      step = { gizmo_id: g.id, from_color: draft.from };
    }
    setConversions(prev => [...prev, step]);
    setConvDraft(prev => ({ ...prev, [g.id]: {} }));
  };

  const removeConversion = (idx) => {
    setConversions(prev => prev.filter((_, i) => i !== idx));
  };

  const summary = ENERGY_COLORS
    .filter(c => working[c] > 0)
    .map(c => `${working[c]}${COLOR_LABEL[c]}`)
    .join(' + ') || '0';

  return (
    <div className="modal-overlay">
      <div className="modal-box build-modal">
        <h3>建造装置 #{gizmo.id}（T{gizmo.tier}，{gizmo.vp}VP）</h3>

        <div className="cost-line">
          <span>成本：</span>
          <CostRow cost={cost} size={20} />
          {costTotal < originalTotal && (
            <span className="discount-note">（原 {originalTotal}，折扣 -{originalTotal - costTotal}）</span>
          )}
        </div>

        <div className="pay-section">
          <h4>支付能量</h4>
          <div className="energy-steppers">
            {ENERGY_COLORS.map(c => (
              <div key={c} className={`stepper ${(playerEnergy[c] || 0) === 0 ? 'stepper-empty' : ''}`}>
                <EnergyBall color={c} size={22} />
                <button className="btn-step" onClick={() => adjustEnergy(c, -1)}
                  disabled={(energy[c] || 0) === 0}>−</button>
                <span className="step-value">{energy[c] || 0}</span>
                <button className="btn-step" onClick={() => adjustEnergy(c, 1)}
                  disabled={(energy[c] || 0) >= (playerEnergy[c] || 0)}>＋</button>
                <span className="step-have">/{playerEnergy[c] || 0}</span>
              </div>
            ))}
          </div>
        </div>

        {converters.length > 0 && (
          <div className="pay-section">
            <h4>转换器（每回合每张一次）</h4>
            {converters.map(g => {
              const left = usesLeft(g);
              const exhausted = Array.isArray(left) ? left.length === 0 : left <= 0;
              const draft = convDraft[g.id] || {};
              const type = g.effect.type;
              return (
                <div key={g.id} className={`converter-row ${exhausted ? 'exhausted' : ''}`}>
                  <span className="converter-name">
                    #{g.id} {EFFECT_LABEL[type](g.effect)}
                    {!exhausted && Array.isArray(left) === false && type === 'converter_any' && ` (余${left})`}
                  </span>
                  {!exhausted && (
                    <span className="converter-controls">
                      {type === 'converter_wild' && (
                        <ColorPick label="从" value={draft.from}
                          onPick={(c) => setConvDraft(p => ({ ...p, [g.id]: { ...draft, from: c } }))} />
                      )}
                      {type === 'converter_double' && (
                        <ColorPick label="倍增" value={draft.from} colors={left}
                          onPick={(c) => setConvDraft(p => ({ ...p, [g.id]: { ...draft, from: c } }))} />
                      )}
                      {type !== 'converter_double' && (
                        <ColorPick label="变为" value={draft.to}
                          onPick={(c) => setConvDraft(p => ({ ...p, [g.id]: { ...draft, to: c } }))} />
                      )}
                      <button className="btn-primary btn-xs" onClick={() => addConversion(g)}>添加</button>
                    </span>
                  )}
                </div>
              );
            })}
            {conversions.length > 0 && (
              <div className="conversion-chain">
                {conversions.map((s, i) => {
                  const conv = converters.find(g => g.id === s.gizmo_id);
                  const isDouble = conv?.effect?.type === 'converter_double';
                  return (
                    <span key={i} className="conversion-chip">
                      {isDouble
                        ? `${COLOR_LABEL[s.from_color]}×2`
                        : `${COLOR_LABEL[s.from_color]}→${COLOR_LABEL[s.to_color]}`}
                      <button className="chip-x" onClick={() => removeConversion(i)}>✕</button>
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className={`pay-preview ${exact ? 'ok' : 'not-ok'}`}>
          {valid
            ? <>转换后合计：{summary} {exact ? '✓ 恰好支付' : '（需与成本完全一致）'}</>
            : '转换链顺序无效：某一步缺少可转换的能量'}
        </div>

        <div className="modal-actions">
          <button className="btn-primary" disabled={!exact}
            onClick={() => onConfirm({ energy, conversions })}>确认建造</button>
          <button className="btn-secondary" onClick={onCancel}>取消</button>
        </div>
      </div>
    </div>
  );
}

function ColorPick({ label, value, onPick, colors = ENERGY_COLORS }) {
  return (
    <span className="color-pick">
      <span className="pick-label">{label}</span>
      {colors.map(c => (
        <button key={c}
          className={`pick-dot ${COLOR_CLASS[c]} ${value === c ? 'picked' : ''}`}
          title={COLOR_LABEL[c]}
          onClick={() => onPick(c)}
        />
      ))}
    </span>
  );
}

// -----------------------------------------------------------------------
// Main board
// -----------------------------------------------------------------------

export default function GizmosBoard({
  gameMeta, gameState, currentUserId, onAction, onLeave, actionError,
}) {
  const [busy, setBusy] = useState(false);
  const [buildTarget, setBuildTarget] = useState(null);  // {gizmo, source}
  const [localError, setLocalError] = useState('');

  const currentPlayerId = gameState.turn_order[gameState.current_player_index];
  const isMyTurn = currentPlayerId === currentUserId;
  const myState = gameState.players[currentUserId] || null;
  const isFinished = gameMeta.status === 'finished';
  const usedIds = gameState.turn_used_gizmo_ids || [];

  const pendingQueue = gameState.pending_queue || [];
  const researchState = gameState.research_state || null;
  const firstPending = pendingQueue[0] || null;
  const awaitingEnergyChoice = firstPending?.choice_type === 'energy_color';
  const hasPending = (t) => pendingQueue.some(c => c.choice_type === t);
  const awaitingExtraPick = hasPending('extra_pick');
  const awaitingExtraFile = hasPending('extra_file');
  const awaitingExtraResearch = hasPending('extra_research');
  const freeBuildPending = pendingQueue.find(c => c.choice_type === 'extra_build_free') || null;
  const triggerPendings = pendingQueue.filter(
    c => c.choice_type === 'give_random' || c.choice_type === 'give_vp');
  const awaitingResearch = !!researchState;
  const awaitingResolution = pendingQueue.length > 0 || awaitingResearch;

  const negatedFile = (myState?.negated_actions || []).includes('FILE');
  const negatedResearch = (myState?.negated_actions || []).includes('RESEARCH');
  const storageFull = myState ? totalEnergy(myState) >= myState.energy_capacity : false;

  const userMap = useMemo(() => {
    const m = {};
    (gameMeta.players || []).forEach(p => { m[p.user] = p.username; });
    return m;
  }, [gameMeta.players]);

  const winner = useMemo(() => {
    if (!isFinished) return null;
    return Object.entries(gameState.players).reduce((best, [pid, ps]) => {
      const vp = computeVp(ps);
      if (!best || vp > best.vp || (vp === best.vp && ps.built_gizmos.length < best.count)) {
        return { id: Number(pid), vp, count: ps.built_gizmos.length };
      }
      return best;
    }, null);
  }, [isFinished, gameState.players]);

  const act = useCallback(async (type, payload) => {
    setLocalError('');
    setBusy(true);
    try {
      await onAction(type, payload);
    } catch (err) {
      setLocalError(err?.response?.data?.detail || '操作失败');
    } finally {
      setBusy(false);
    }
  }, [onAction]);

  // ---- Actions ----
  const canPickNow = isMyTurn && !busy && !isFinished && !storageFull
    && (!awaitingResolution || awaitingExtraPick);

  const handlePickSphere = (ringIndex) => {
    if (!canPickNow) return;
    act('pick_energy', awaitingExtraPick
      ? { ring_index: ringIndex, is_extra: true }
      : { ring_index: ringIndex });
  };

  const handleFileGizmo = (gizmo) => {
    if (!isMyTurn || busy || negatedFile) return;
    if (awaitingResolution && !awaitingExtraFile) return;
    act('file_gizmo', { gizmo_id: gizmo.id, is_extra: awaitingExtraFile });
  };

  const handleBuildGizmo = (gizmo, source) => {
    if (!isMyTurn || busy || awaitingResolution) return;
    setBuildTarget({ gizmo, source });
  };

  const confirmBuild = (payment) => {
    act('build_gizmo', { gizmo_id: buildTarget.gizmo.id, payment, source: buildTarget.source });
    setBuildTarget(null);
  };

  const handleFreeBuild = (gizmo) => {
    if (!isMyTurn || busy || !freeBuildPending) return;
    act('build_gizmo', { gizmo_id: gizmo.id, is_extra_free: true });
  };

  const handleResearchStart = (tier, isExtra = false) => {
    if (!isMyTurn || busy || negatedResearch) return;
    if (awaitingResolution && !isExtra) return;
    act('research_start', isExtra ? { tier, is_extra: true } : { tier });
  };

  const handleResearchAction = (action, gizmo, payment) => {
    act('research_resolve', {
      action,
      gizmo_id: gizmo?.id,
      payment: payment || undefined,
    });
  };

  const handleResolveChoice = (color) => act('resolve_choice', { colors: [color] });
  const handleSkipExtra = (type) => act('skip_pending', { pending_type: type });
  const handleActivateTrigger = (pending) => act('activate_pending', {
    pending_type: pending.choice_type,
    source_gizmo_id: pending.source_gizmo_id ?? undefined,
  });
  const handleGiveUp = () => {
    if (!isMyTurn || busy || awaitingResolution) return;
    act('give_up', {});
  };

  const displayError = localError || actionError;

  const turnText = isFinished
    ? `游戏结束！胜者：${userMap[winner?.id] || winner?.id}（${winner?.vp}VP）`
    : isMyTurn
      ? awaitingResolution
        ? awaitingEnergyChoice ? '请选择能量颜色'
          : triggerPendings.length > 0 ? '装置效果待触发'
          : awaitingExtraPick ? '额外拾取机会'
          : awaitingExtraFile ? '额外归档机会'
          : awaitingExtraResearch ? '额外研究机会'
          : freeBuildPending ? `免费建造 T${freeBuildPending.tier}`
          : '请完成研究'
        : '你的回合'
      : `等待 ${userMap[currentPlayerId] || currentPlayerId} 行动`;

  return (
    <div className="gizmos-board">
      {/* Header */}
      <div className="gizmos-header">
        <button className="btn-secondary btn-sm" onClick={onLeave}>← 离开</button>
        <span className={`turn-indicator ${isMyTurn && !isFinished ? 'my-turn' : ''}`}>{turnText}</span>
        {gameState.game_over_trigger != null && !isFinished && (
          <span className="endgame-badge">终局回合</span>
        )}
        <span className="round-badge">第 {gameState.round_number} 轮</span>
      </div>

      {displayError && <div className="action-error">{displayError}</div>}

      {/* Pending energy choice */}
      {awaitingEnergyChoice && isMyTurn && (
        <div className="choice-panel pulse">
          <p>选择 {firstPending.amount} 个能量（来自装置效果）：</p>
          <div className="choice-buttons">
            {ENERGY_COLORS.map(c => (
              <button key={c} className={`btn-energy ${COLOR_CLASS[c]}`}
                onClick={() => handleResolveChoice(c)}>
                {COLOR_LABEL[c]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Manual trigger activation banners */}
      {isMyTurn && !isFinished && triggerPendings.length > 0 && (
        <div className="choice-panel extra-banner pulse">
          {triggerPendings.map((p, i) => (
            <span key={`${p.choice_type}-${p.source_gizmo_id}-${i}`} className="banner-item">
              ⚙️ 装置 #{p.source_gizmo_id} 效果：
              {p.choice_type === 'give_random'
                ? `盲抽 ${p.amount} 个能量`
                : `+${p.amount} VP 标记`}
              <button className="btn-primary btn-sm"
                onClick={() => handleActivateTrigger(p)}>触发</button>
              <button className="btn-ghost btn-sm"
                onClick={() => handleSkipExtra(p.choice_type)}>放弃</button>
            </span>
          ))}
        </div>
      )}

      {/* Extra action banners */}
      {isMyTurn && !isFinished && (awaitingExtraPick || awaitingExtraFile
        || awaitingExtraResearch || freeBuildPending) && (
        <div className="choice-panel extra-banner pulse">
          {awaitingExtraPick && (
            <span className="banner-item">
              ⚡ 额外拾取：点击下方能量球{storageFull && '（储能已满，只能跳过）'}
              <button className="btn-ghost btn-sm" onClick={() => handleSkipExtra('extra_pick')}>跳过</button>
            </span>
          )}
          {awaitingExtraFile && (
            <span className="banner-item">
              ⚡ 额外归档：点击卡牌上的「归档」
              <button className="btn-ghost btn-sm" onClick={() => handleSkipExtra('extra_file')}>跳过</button>
            </span>
          )}
          {awaitingExtraResearch && !awaitingResearch && (
            <span className="banner-item">
              ⚡ 额外研究：
              {[1, 2, 3].map(t => (
                <button key={t} className="btn-primary btn-sm"
                  disabled={negatedResearch}
                  onClick={() => handleResearchStart(t, true)}>研究 T{t}</button>
              ))}
              <button className="btn-ghost btn-sm" onClick={() => handleSkipExtra('extra_research')}>跳过</button>
            </span>
          )}
          {freeBuildPending && (
            <span className="banner-item">
              ⚡ 免费建造一张 T{freeBuildPending.tier}：点击卡牌上的「免费建造」
              <button className="btn-ghost btn-sm" onClick={() => handleSkipExtra('extra_build_free')}>跳过</button>
            </span>
          )}
        </div>
      )}

      {/* Research panel */}
      {awaitingResearch && isMyTurn && (
        <ResearchPanel
          researchState={researchState}
          myState={myState}
          usedIds={usedIds}
          negatedFile={negatedFile}
          onBuild={(g, payment) => handleResearchAction('build', g, payment)}
          onFile={(g) => handleResearchAction('file', g)}
          onPass={() => handleResearchAction('pass')}
        />
      )}

      {/* Dispenser */}
      <div className="dispenser-section">
        <div className="dispenser-head">
          <h3>能量分配器</h3>
          <span className="pool-count">牌堆剩余 {gameState.dispenser_pool.length} 球</span>
          {storageFull && isMyTurn && !isFinished && (
            <span className="storage-full-hint">储能已满，无法拾取</span>
          )}
        </div>
        <div className="dispenser-track">
          <div className="dispenser-next" title="即将补入能量列的球">
            <span className="next-label">下一个</span>
            {gameState.dispenser_pool.length > 0 && (
              <span className="next-ball">
                <EnergyBall color={gameState.dispenser_pool[0]} size={30} />
              </span>
            )}
            <span className="next-arrow">➜</span>
          </div>
          <div className="dispenser-ring">
            {gameState.dispenser_ring.map((color, i) => (
              <EnergyBall
                key={i}
                color={color}
                size={46}
                onClick={() => handlePickSphere(i)}
                clickable={canPickNow}
              />
            ))}
          </div>
        </div>
        {isMyTurn && !awaitingResolution && !isFinished && !busy && (
          <button className="btn-ghost btn-sm give-up-btn" onClick={handleGiveUp}
            title="放弃本回合，无任何补偿">
            放弃回合
          </button>
        )}
      </div>

      {/* Gizmo rows */}
      <div className="tiers-section">
        {[3, 2, 1].map(tier => (
          <GizmoRow
            key={tier}
            tier={tier}
            gizmos={gameState[`tier${tier}_visible`]}
            deckCount={(gameState[`tier${tier}_deck`] || []).length}
            isMyTurn={isMyTurn && !isFinished}
            canBuild={!awaitingResolution}
            canFile={(!awaitingResolution || awaitingExtraFile) && !negatedFile}
            canResearch={!awaitingResolution && !negatedResearch}
            freeBuildTier={freeBuildPending?.tier ?? null}
            onBuild={(g) => handleBuildGizmo(g, 'table')}
            onFreeBuild={handleFreeBuild}
            onFile={handleFileGizmo}
            onResearch={() => handleResearchStart(tier)}
          />
        ))}
      </div>

      {/* Players */}
      <div className="players-section">
        {gameState.turn_order.map(pid => (
          <PlayerDashboard
            key={pid}
            playerState={gameState.players[pid]}
            username={userMap[pid] || String(pid)}
            isCurrentPlayer={pid === currentPlayerId}
            isMe={pid === currentUserId}
            onBuildFromArchive={(g) => handleBuildGizmo(g, 'archive')}
            canBuild={isMyTurn && pid === currentUserId && !awaitingResolution && !isFinished}
          />
        ))}
      </div>

      {/* Action log */}
      <GameLogPanel log={gameState.log || []} userMap={userMap} />

      {/* Action toasts (what just happened) */}
      <ActionToasts log={gameState.log || []} userMap={userMap} currentUserId={currentUserId} />

      {/* Modals */}
      {buildTarget && (
        <BuildModal
          gizmo={buildTarget.gizmo}
          source={buildTarget.source}
          myState={myState}
          usedIds={usedIds}
          onConfirm={confirmBuild}
          onCancel={() => setBuildTarget(null)}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// GizmoRow
// -----------------------------------------------------------------------

function GizmoRow({
  tier, gizmos, deckCount, isMyTurn, canBuild, canFile, canResearch,
  freeBuildTier, onBuild, onFreeBuild, onFile, onResearch,
}) {
  const freeHere = freeBuildTier === tier;
  return (
    <div className={`gizmo-row tier-row-${tier} ${freeHere ? 'free-build-row' : ''}`}>
      <div className="row-header">
        <span className="tier-label">T{tier}</span>
        <span className="deck-count">牌库 {deckCount}</span>
        {isMyTurn && canResearch && deckCount > 0 && (
          <button className="btn-secondary btn-sm" onClick={onResearch}>🔍 研究</button>
        )}
      </div>
      <div className="cards-row">
        {gizmos.map(g => (
          <div key={g.id} className="gizmo-card-wrapper">
            <GizmoCard gizmo={g} />
            {isMyTurn && (
              <div className="card-actions">
                {freeHere && (
                  <button className="btn-success btn-xs" onClick={() => onFreeBuild(g)}>免费建造</button>
                )}
                {!freeHere && canBuild && (
                  <button className="btn-primary btn-xs" onClick={() => onBuild(g)}>建造</button>
                )}
                {!freeHere && canFile && (
                  <button className="btn-secondary btn-xs" onClick={() => onFile(g)}>归档</button>
                )}
              </div>
            )}
          </div>
        ))}
        {gizmos.length === 0 && <span className="empty-row">（空）</span>}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// PlayerDashboard — mirrors the official player dashboard layout:
// [Upgrades | Converters | File | Pick | Build] columns + Archive at the side
// -----------------------------------------------------------------------

function dashboardColumn(gizmo) {
  if (gizmo.trigger === 'FILE') return 'file';
  if (gizmo.trigger === 'PICK') return 'pick';
  if (gizmo.trigger === 'BUILD') return 'build';
  const category = EFFECT_CATEGORY[gizmo.effect?.type] || 'upgrade';
  return category === 'converter' ? 'converter' : 'upgrade';
}

const DASHBOARD_COLUMNS = [
  { key: 'upgrade', icon: '➕', label: '升级' },
  { key: 'converter', icon: '▶', label: '转换器' },
  { key: 'file', icon: '📁', label: '归档' },
  { key: 'pick', icon: '🖐', label: '拾取' },
  { key: 'build', icon: '🔧', label: '建造' },
];

function PlayerDashboard({ playerState, username, isCurrentPlayer, isMe, onBuildFromArchive, canBuild }) {
  const [expanded, setExpanded] = useState(isMe);
  const vp = computeVp(playerState);
  const tier3Count = playerState.built_gizmos.filter(g => g.tier === 3).length;
  const negated = playerState.negated_actions || [];

  const columns = useMemo(() => {
    const cols = { upgrade: [], converter: [], file: [], pick: [], build: [] };
    for (const g of playerState.built_gizmos) cols[dashboardColumn(g)].push(g);
    return cols;
  }, [playerState.built_gizmos]);

  return (
    <div className={`player-panel ${isCurrentPlayer ? 'active-player' : ''} ${isMe ? 'my-panel' : ''} ${expanded ? 'expanded' : ''}`}>
      <div className="player-header">
        {isCurrentPlayer && <span className="turn-arrow">▶</span>}
        <strong>{username}{isMe ? '（我）' : ''}</strong>
        <span className="player-vp" title={`基础${playerState.vp} + 标记${playerState.vp_tokens || 0} + 公式`}>
          {vp} VP
        </span>
        <button className="btn-ghost btn-xs" onClick={() => setExpanded(e => !e)}>
          {expanded ? '▲收起' : '▼展开'}
        </button>
      </div>

      <div className="player-energy">
        {ENERGY_COLORS.map(c => (
          <span key={c} className={`energy-count ${COLOR_CLASS[c]}`}>
            {playerState.energy[c] || 0}
          </span>
        ))}
        <span className="energy-total">
          {totalEnergy(playerState)}/{playerState.energy_capacity}
        </span>
      </div>

      {/* Official dashboard stats strip: storage limit / file limit / research amount */}
      <div className="dashboard-stats">
        <span className="stat-chip" title="储能上限">⚪ {playerState.energy_capacity}</span>
        <span className="stat-chip" title="档案上限">📁 {playerState.archive_capacity}</span>
        <span className="stat-chip" title="研究数量">🔍 {playerState.research_depth}</span>
        <span className="stat-chip" title="已建装置数（含起始装置）">🔧 {playerState.built_gizmos.length}/16</span>
        <span className="stat-chip" title="三级装置数">T3 {tier3Count}/4</span>
        <span className="stat-chip" title="VP标记">🏅 {playerState.vp_tokens || 0}</span>
        {negated.map(t => (
          <span key={t} className="negated-badge">禁{TARGET_LABEL[t] || t}</span>
        ))}
      </div>

      {expanded && (
        <div className="dashboard-body">
          <div className="dashboard-columns">
            {DASHBOARD_COLUMNS.map(col => (
              <div key={col.key} className={`dashboard-column col-${col.key}`}>
                <div className="column-header" title={col.label}>
                  <span className="column-icon">{col.icon}</span> {col.label}
                </div>
                <div className="column-cards">
                  {columns[col.key].map(g => (
                    <GizmoCard key={g.id} gizmo={g} />
                  ))}
                  {columns[col.key].length === 0 && <span className="column-empty">—</span>}
                </div>
              </div>
            ))}
            <div className="dashboard-column archive-column">
              <div className="column-header">🗄 档案 {playerState.archived_gizmos.length}/{playerState.archive_capacity}</div>
              <div className="column-cards">
                {playerState.archived_gizmos.map(g => (
                  <div key={g.id} className="archive-card-wrapper">
                    <GizmoCard gizmo={g} />
                    {canBuild && (
                      <button className="btn-primary btn-xs"
                        onClick={() => onBuildFromArchive(g)}>建造</button>
                    )}
                  </div>
                ))}
                {playerState.archived_gizmos.length === 0 && <span className="column-empty">（空）</span>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Action log — human-readable entries, newest first
// -----------------------------------------------------------------------

function describeLogEntry(entry, userMap) {
  const name = userMap[entry.player_id] || `玩家${entry.player_id}`;
  const colorDot = (c) => COLOR_LABEL[c] || c;
  switch (entry.action) {
    case 'pick_energy':
      return `${name} 拾取了 ${colorDot(entry.color)} 能量`;
    case 'file_gizmo':
      return `${name} 归档了装置 #${entry.gizmo_id}`;
    case 'build_gizmo':
      return `${name} ${entry.free ? '免费建造' : '建造'}了 T${entry.tier} 装置 #${entry.gizmo_id}`
        + (entry.source === 'archive' ? '（来自档案）' : '');
    case 'research_start':
      return `${name} 研究了 T${entry.tier} 牌库`;
    case 'research_pass':
      return `${name} 放回了全部研究卡`;
    case 'research_file':
      return `${name} 归档了研究到的装置 #${entry.gizmo_id}`;
    case 'research_build':
      return `${name} 建造了研究到的 T${entry.tier} 装置 #${entry.gizmo_id}`;
    case 'give_up':
      return `${name} 放弃了本回合`;
    case 'resolve_choice':
      return `${name} 选择了能量：${(entry.colors || []).map(colorDot).join('、')}`;
    case 'trigger_random':
      return `${name} 触发装置 #${entry.gizmo_id}，盲抽了 ${entry.amount} 个能量`;
    case 'trigger_vp':
      return `${name} 触发装置 #${entry.gizmo_id}，获得 ${entry.amount} VP标记`;
    default:
      if (entry.action?.startsWith('skip_')) {
        return `${name} 放弃了一次待触发效果`;
      }
      return `${name} ${entry.action}`;
  }
}

function GameLogPanel({ log, userMap }) {
  const [open, setOpen] = useState(true);
  const entries = log.slice(-60).reverse();
  return (
    <div className="game-log-panel">
      <div className="log-header" onClick={() => setOpen(o => !o)}>
        <strong>📜 行动记录</strong>
        <span className="log-toggle">{open ? '▼' : '▲'}</span>
      </div>
      {open && (
        <div className="log-entries">
          {entries.map((e, i) => (
            <div key={log.length - i} className={`log-entry ${i === 0 ? 'latest' : ''}`}>
              <span className="log-round">R{e.round}</span> {describeLogEntry(e, userMap)}
            </div>
          ))}
          {entries.length === 0 && <div className="log-entry">暂无记录</div>}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------
// Action toasts — animated banner for actions as they happen
// -----------------------------------------------------------------------

function ActionToasts({ log, userMap, currentUserId }) {
  const [toasts, setToasts] = useState([]);   // [{id, text, kind}]
  const seenRef = useRef(null);               // last log length we've shown

  useEffect(() => {
    if (seenRef.current === null) {
      // First render: don't replay the whole history.
      seenRef.current = log.length;
      return;
    }
    if (log.length <= seenRef.current) {
      seenRef.current = Math.min(seenRef.current, log.length);
      return;
    }
    const fresh = log.slice(seenRef.current)
      .filter(e => e.player_id !== currentUserId);  // your own actions need no echo
    seenRef.current = log.length;
    if (!fresh.length) return;
    const newToasts = fresh.map((e, i) => ({
      id: `${Date.now()}-${i}`,
      text: describeLogEntry(e, userMap),
      kind: e.action,
    }));
    setToasts(prev => [...prev.slice(-3), ...newToasts]);
    newToasts.forEach(t => {
      setTimeout(() => {
        setToasts(prev => prev.filter(x => x.id !== t.id));
      }, 4000);
    });
  }, [log, userMap, currentUserId]);

  if (!toasts.length) return null;
  return (
    <div className="action-toasts">
      {toasts.map(t => (
        <div key={t.id} className={`action-toast toast-${t.kind}`}>{t.text}</div>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------
// ResearchPanel
// -----------------------------------------------------------------------

function ResearchPanel({ researchState, myState, usedIds, negatedFile, onBuild, onFile, onPass }) {
  const [buildTarget, setBuildTarget] = useState(null);

  return (
    <div className="research-panel">
      <h3>🔍 研究 T{researchState.tier} —— 选择一张建造/归档，或全部放回</h3>
      <div className="research-cards">
        {researchState.cards.map(g => (
          <div key={g.id} className="research-card-wrapper">
            <GizmoCard gizmo={g} />
            <div className="card-actions">
              <button className="btn-primary btn-xs"
                onClick={() => setBuildTarget(g)}>建造</button>
              {!negatedFile && (
                <button className="btn-secondary btn-xs"
                  onClick={() => onFile(g)}>归档</button>
              )}
            </div>
          </div>
        ))}
      </div>
      <button className="btn-secondary" onClick={onPass}>放回全部</button>

      {buildTarget && (
        <BuildModal
          gizmo={buildTarget}
          source="research"
          myState={myState}
          usedIds={usedIds}
          onConfirm={(payment) => { onBuild(buildTarget, payment); setBuildTarget(null); }}
          onCancel={() => setBuildTarget(null)}
        />
      )}
    </div>
  );
}
